import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';

import { DataTable, EmptyState } from '../../../components/shared';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const baseChartOptions = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, background: 'transparent' },
  dataLabels: { enabled: false },
  grid: { borderColor: '#e3e8ef', strokeDashArray: 4, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
  tooltip: { theme: 'light' },
};

// Small badge for shipment type / dispatch
function MiniChip({ label, bg, color }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, lineHeight: 1 }}>{label}</Typography>
    </Box>
  );
}

const shipmentType = (sc) => sc.shipmentNumber?.startsWith('VSPH-') ? 'Vendor' : 'Dispatch';

// ─── Tab summary bar ──────────────────────────────────────────────────────────
function TabSummaryBar({ costs }) {
  const total    = costs.reduce((s, c) => s + c.cost, 0);
  const dispatch = costs.filter((c) => shipmentType(c) === 'Dispatch').length;
  const vendor   = costs.filter((c) => shipmentType(c) === 'Vendor').length;
  const avg      = costs.length ? total / costs.length : 0;
  const stats = [
    { label: 'Total Costs',             value: fmt(total) },
    { label: 'Dispatch Shipments',      value: dispatch    },
    { label: 'Replenishment',             value: vendor      },
    { label: 'Avg Cost per Shipment',   value: fmt(avg)    },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 3, px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
      {stats.map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--grey-900)' }}>{s.value}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────
export default function ShippingCostsTab({ shippingCosts }) {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ field: 'billedDate', direction: 'desc' });

  const handleSort = (field) => setSortConfig((prev) => ({
    field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const sorted = useMemo(
    () =>
      [...shippingCosts].sort((a, b) => {
        const av = a[sortConfig.field] ?? '';
        const bv = b[sortConfig.field] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }),
    [shippingCosts, sortConfig],
  );

  // Chart: cost by carrier
  const carrierData = useMemo(() => {
    const map = {};
    shippingCosts.forEach((c) => {
      map[c.carrier] = (map[c.carrier] || 0) + c.cost;
    });
    return Object.entries(map).map(([carrier, total]) => ({ carrier, total }));
  }, [shippingCosts]);

  const chartOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar' },
    xaxis: { categories: carrierData.map((d) => d.carrier), labels: { style: { fontSize: '11px', colors: '#607d8b' } } },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: '#607d8b' },
        formatter: (v) => `$${v.toFixed(0)}`,
      },
    },
    colors: ['#009688'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => fmt(v) } },
  };
  const chartSeries = [{ name: 'Shipping Cost', data: carrierData.map((d) => parseFloat(d.total.toFixed(2))) }];

  const columns = [
    {
      field: 'shipmentNumber',
      headerName: 'Shipment #',
      sortable: true,
      renderCell: (row) => {
        const type = shipmentType(row);
        const path = type === 'Vendor'
          ? `/shipments/replenishment-shipments/${row.shipmentId}`
          : `/shipments/dispatch/${row.shipmentId}`;
        return (
          <Typography
            onClick={(e) => { e.stopPropagation(); navigate(path); }}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {row.shipmentNumber}
          </Typography>
        );
      },
    },
    {
      field: '_type',
      headerName: 'Type',
      sortable: false,
      renderCell: (row) => {
        const type = shipmentType(row);
        return type === 'Vendor'
          ? <MiniChip label="Vendor"   bg="#EDE7F6" color="#4527A0" />
          : <MiniChip label="Dispatch" bg="#E3F2FD" color="#1565C0" />;
      },
    },
    { field: 'carrier',    headerName: 'Carrier',    sortable: true },
    {
      field: 'cost',
      headerName: 'Cost',
      sortable: true,
      renderCell: (row) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{fmt(row.cost)}</Typography>
      ),
    },
    { field: 'billedDate', headerName: 'Billed Date', sortable: true },
    {
      field: 'ticketNumber',
      headerName: 'Linked Ticket #',
      sortable: false,
      renderCell: (row) =>
        row.ticketNumber ? (
          <Typography
            onClick={(e) => { e.stopPropagation(); navigate(`/orders/parts/${row.partOrderId}`); }}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {row.ticketNumber}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
        ),
    },
  ];

  return (
    <>
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Shipping Costs
        </Typography>
      </Box>

      <TabSummaryBar costs={shippingCosts} />

      {shippingCosts.length === 0 ? (
        <EmptyState title="No shipping costs" description="Try adjusting your date range." />
      ) : (
        <>
          {/* Chart */}
          <Box
            sx={{ px: 3,
              pt: 2,
              pb: 1,
              borderBottom: '1px solid var(--grey-200)',
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: 1 }}>
              Cost by Carrier
            </Typography>
            <ReactApexChart
              type="bar"
              options={chartOptions}
              series={chartSeries}
              height={180}
              width="100%"
            />
          </Box>

          <Box sx={{ mx: 3, mt: 3, mb: 3, border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            <DataTable columns={columns} data={sorted} sortConfig={sortConfig} onSort={handleSort} />
          </Box>
        </>
      )}
    </>
  );
}
